'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import WorldwayNav from '@/components/WorldwayNav';

const TIERS = [
  {
    name: 'Voyager',
    price: '$2,400',
    period: 'per year',
    color: 'from-slate-400 to-slate-600',
    badge: '🌐',
    benefits: [
      'Priority booking access',
      'Dedicated travel consultant',
      'Complimentary airport lounge access (4x/year)',
      'Room upgrades when available',
      '5% loyalty credit on all bookings',
      'Access to member-only rates',
      'Annual travel review & planning session',
    ],
    cta: 'Join Voyager',
  },
  {
    name: 'Luminary',
    price: '$6,800',
    period: 'per year',
    color: 'from-amber-400 to-amber-600',
    badge: '⭐',
    popular: true,
    benefits: [
      'Everything in Voyager, plus:',
      'Guaranteed suite upgrades at partner hotels',
      'Unlimited airport lounge access',
      '10% loyalty credit on all bookings',
      'Complimentary travel insurance (worldwide)',
      'Private jet empty leg alerts',
      'Quarterly destination reports',
      'Concierge response within 30 minutes',
      'Birthday & anniversary surprises',
    ],
    cta: 'Join Luminary',
  },
  {
    name: 'Sovereign',
    price: 'By invitation',
    period: 'ultra-exclusive',
    color: 'from-yellow-300 to-amber-500',
    badge: '👑',
    benefits: [
      'Everything in Luminary, plus:',
      'Personal travel director (dedicated 24/7)',
      '15% loyalty credit on all bookings',
      'Complimentary private jet hours (20 hrs/year)',
      'Access to off-market villas & residences',
      'Exclusive event invitations worldwide',
      'Concierge response within 10 minutes',
      'Annual luxury gift',
      'White-glove document & visa service',
      'Family membership included',
    ],
    cta: 'Request Invitation',
  },
];

const PERKS = [
  { icon: '🏨', title: 'Hotel Benefits', desc: 'Guaranteed upgrades, early check-in, late check-out and welcome amenities at 2,000+ partner properties.' },
  { icon: '✈', title: 'Aviation Privileges', desc: 'Priority access to private jet empty legs, partner airline upgrades and lounge access worldwide.' },
  { icon: '🎭', title: 'Exclusive Events', desc: 'Private gallery openings, chef\'s table dinners, fashion week access and cultural events.' },
  { icon: '💎', title: 'Curated Gifts', desc: 'Seasonal luxury gifts, destination hampers and bespoke experiences delivered to your door.' },
  { icon: '🤖', title: 'AI Concierge Priority', desc: 'Priority queue access to our AI and human concierge team — any request, any time.' },
  { icon: '📊', title: 'Travel Intelligence', desc: 'Quarterly destination trend reports, visa updates and exclusive travel insights.' },
];

const FAQS = [
  { q: 'Can I upgrade my membership tier?', a: 'Yes — you can upgrade at any time. We\'ll credit the pro-rated value of your current membership towards the new tier.' },
  { q: 'Is membership transferable?', a: 'Voyager and Luminary memberships are personal. Sovereign membership includes family coverage for up to 4 members.' },
  { q: 'How does loyalty credit work?', a: 'Loyalty credit accumulates on every booking and can be redeemed against future travel. Credits never expire while your membership is active.' },
  { q: 'What is the cancellation policy?', a: 'Memberships can be cancelled within 30 days for a full refund. After 30 days, a pro-rated refund is available minus any benefits used.' },
];

export default function MembershipPage() {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      <WorldwayNav />
      {/* Hero */}
      <section className="relative pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.2) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <p className="label-caps mb-3 text-[var(--primary)]">Membership</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
            Travel Without<br />
            <span className="gold-text-gradient">Compromise.</span>
          </h1>
          <p className="text-lg text-[var(--muted-foreground)] mb-8 max-w-2xl mx-auto">
            Join an exclusive community of discerning travellers. Three tiers of membership — each unlocking a world of privileges, priority access and personalised service.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button onClick={() => router?.push('/auth/register')} className="btn-primary px-8 py-3 rounded-xl text-sm font-semibold">
              Become a Member
            </button>
            <Link href="/ai-concierge" className="px-8 py-3 rounded-xl text-sm font-semibold border border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--primary)]/50 hover:text-[var(--primary)] transition-all">
              Ask Concierge
            </Link>
          </div>
        </div>
      </section>
      {/* Membership Tiers */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2 text-center">Membership Tiers</p>
          <h2 className="text-2xl font-bold mb-10 text-center" style={{ fontFamily: 'var(--font-serif)' }}>Choose Your <span className="gold-text-gradient">Level of Access</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS?.map((tier) => (
              <div key={tier?.name} className={`glass-card rounded-2xl p-6 border transition-all ${tier?.popular ? 'border-[rgba(201,168,76,0.5)] shadow-[0_0_30px_rgba(201,168,76,0.1)]' : 'border-[var(--border)] hover:border-[rgba(201,168,76,0.3)]'} relative`}>
                {tier?.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--primary)] text-[var(--primary-foreground)] text-[10px] font-bold uppercase tracking-widest px-4 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                <div className="text-3xl mb-3">{tier?.badge}</div>
                <h3 className="text-xl font-bold mb-1" style={{ fontFamily: 'var(--font-serif)' }}>{tier?.name}</h3>
                <div className="mb-4">
                  <span className="text-2xl font-bold text-[var(--primary)]">{tier?.price}</span>
                  <span className="text-xs text-[var(--muted-foreground)] ml-1">{tier?.period}</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {tier?.benefits?.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-xs text-[var(--muted-foreground)]">
                      <span className="text-[var(--primary)] mt-0.5 flex-shrink-0">✓</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => router?.push(tier?.name === 'Sovereign' ? '/ai-concierge' : '/auth/register')}
                  className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all ${tier?.popular ? 'btn-primary' : 'border border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--primary)]/50 hover:text-[var(--primary)]'}`}
                >
                  {tier?.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Perks */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Member Privileges</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>Benefits That <span className="gold-text-gradient">Elevate Every Journey</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PERKS?.map((p) => (
              <div key={p?.title} className="glass-card rounded-xl p-5">
                <div className="text-3xl mb-3">{p?.icon}</div>
                <h3 className="text-sm font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>{p?.title}</h3>
                <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">{p?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-3xl mx-auto px-6">
          <p className="label-caps mb-2 text-center">FAQ</p>
          <h2 className="text-2xl font-bold mb-8 text-center" style={{ fontFamily: 'var(--font-serif)' }}>Common <span className="gold-text-gradient">Questions</span></h2>
          <div className="space-y-4">
            {FAQS?.map((faq) => (
              <div key={faq?.q} className="glass-card rounded-xl p-5">
                <h3 className="text-sm font-bold mb-2">{faq?.q}</h3>
                <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">{faq?.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Final CTA */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="glass-card rounded-2xl p-10 border border-[rgba(201,168,76,0.2)] text-center">
            <div className="text-4xl mb-4">👑</div>
            <h3 className="text-3xl font-bold mb-3" style={{ fontFamily: 'var(--font-serif)' }}>Ready to Travel Differently?</h3>
            <p className="text-sm text-[var(--muted-foreground)] mb-8 max-w-md mx-auto">Join thousands of discerning travellers who experience the world through WorldwayLuxe. Your first journey awaits.</p>
            <button onClick={() => router?.push('/auth/register')} className="btn-primary px-10 py-3 rounded-xl text-sm font-semibold">
              Create Your Account →
            </button>
          </div>
        </div>
      </section>
      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 WorldwayLuxe · All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/auth/login" className="hover:text-[var(--primary)] transition-colors">Sign In</Link>
            <Link href="/customer-portal" className="hover:text-[var(--primary)] transition-colors">My Account</Link>
            <Link href="/ai-concierge" className="hover:text-[var(--primary)] transition-colors">Concierge</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
